import React, { useEffect, useState, useRef } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useMsal, MsalAuthenticationTemplate } from "@azure/msal-react";
import {
  InteractionType,
  InteractionStatus,
  AccountInfo,
} from "@azure/msal-browser";
import { loginRequest } from "./authConfig.js";
import { acquireAccessToken, getAccountEmail } from "./authToken.js";
// @ts-ignore
import App from "../App.tsx";
// @ts-ignore
import { useGlobalUsers } from "../Features/Services/UserManagement.tsx";
// @ts-ignore
import { useUserContext } from "../Contexts/UserProvider.tsx";
import CircularProgress from "@mui/material/CircularProgress";
import BlockchainDetailsPage from "../Features/Components/Approvals/BlockchainDetailPage";
import LoginRedirect from "./Login.tsx";

const SessionLoader = () => (
  <div className="flex flex-col justify-center items-center h-screen gap-2">
    <CircularProgress />
    <span>Loading Session</span>
  </div>
);

const AuthenticationLayer = () => {
  const { accounts, instance, inProgress } = useMsal();
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  // Always false until createGlobalUsers (and token sync) finish — do not open /app early.
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const bootstrapStartedRef = useRef(false);
  const bootstrappedObjectIdRef = useRef<string | null>(null);
  const {
    replaceUser,
    replaceUserInfo,
    replaceTenant,
    resetUserContext,
  } = useUserContext();
  const { createGlobalUsers } = useGlobalUsers();

  const activeAccount = instance.getActiveAccount() || accounts[0] || null;
  const hasMsalAccount = !!activeAccount;
  const activeObjectId = activeAccount?.localAccountId || null;

  // If MSAL active account changed (e.g. different user after re-login), drop old session.
  useEffect(() => {
    if (!activeObjectId || !bootstrappedObjectIdRef.current) return;
    if (bootstrappedObjectIdRef.current === activeObjectId) return;

    bootstrappedObjectIdRef.current = null;
    bootstrapStartedRef.current = false;
    setIsUserLoaded(false);
    resetUserContext();
  }, [activeObjectId, resetUserContext]);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (isUserLoaded || bootstrapStartedRef.current) {
      return;
    }

    if (!activeAccount) {
      return;
    }

    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }

    bootstrapStartedRef.current = true;
    setIsBootstrapping(true);

    const bootstrapUser = async (account: AccountInfo) => {
      try {
        // Keep the previously selected tenant for the same account (refresh).
        // Drop it when the Entra account changes (logout already clears on sign-out).
        const priorObjectId = localStorage.getItem("objectId");
        const priorTenantCode = localStorage.getItem("tenantCode");

        resetUserContext();
        replaceTenant(null);
        replaceUserInfo(null);

        const response = await acquireAccessToken(instance, account);
        const token = `Bearer ${response.accessToken}`;
        const objectId = response.uniqueId || account.localAccountId;
        const email = getAccountEmail(account);
        const selectedTenantCode =
          priorObjectId && priorObjectId === objectId ? priorTenantCode : null;

        replaceUser({
          userId: null,
          objectId,
          accessToken: token,
          email,
          isGlobalUser: false,
          tenant: selectedTenantCode,
          isLoading: true,
          permissions: [],
        });

        localStorage.setItem("token", token);
        localStorage.setItem("objectId", objectId);
        if (email) {
          localStorage.setItem("email", email);
        }
        if (selectedTenantCode) {
          localStorage.setItem("tenantCode", selectedTenantCode);
        }

        // Must finish before App mounts — otherwise X-User / Authorization are missing on first API calls.
        try {
          const globalUserResponse = await createGlobalUsers(
            objectId,
            response.accessToken
          );

          const resolvedEmail = globalUserResponse?.email || email;
          if (resolvedEmail) {
            localStorage.setItem("email", resolvedEmail);
          }

          replaceUser({
            userId: globalUserResponse?.id ?? null,
            objectId,
            accessToken: token,
            email: resolvedEmail,
            isGlobalUser: true,
            tenant: selectedTenantCode,
            isLoading: false,
            permissions: [],
          });

          replaceUserInfo({
            id: globalUserResponse?.id,
            email: resolvedEmail,
            firstName: globalUserResponse?.firstName,
            lastName: globalUserResponse?.lastName,
          });
        } catch (apiError) {
          console.warn("Global user sync failed — continuing with MSAL session:", apiError);
          replaceUser({
            userId: null,
            objectId,
            accessToken: token,
            email,
            isGlobalUser: true,
            tenant: selectedTenantCode,
            isLoading: false,
            permissions: [],
          });
          replaceUserInfo({
            id: null,
            email,
            firstName: null,
            lastName: null,
          });
        }

        bootstrappedObjectIdRef.current = objectId;
        // Only open /app after createGlobalUsers has settled (success or handled failure).
        setIsUserLoaded(true);
        sessionStorage.removeItem("msal_redirect_guard");
      } catch (error) {
        console.error("Auth bootstrap error:", error);
        // Token acquisition failed — allow retry on next effect cycle; do not mount App yet.
        bootstrapStartedRef.current = false;
        replaceUser({
          userId: null,
          objectId: null,
          accessToken: null,
          email: null,
          isGlobalUser: false,
          tenant: null,
          isLoading: false,
          permissions: [],
        });
      } finally {
        setIsBootstrapping(false);
      }
    };

    bootstrapUser(activeAccount);
  }, [
    accounts,
    activeAccount,
    instance,
    inProgress,
    isUserLoaded,
    createGlobalUsers,
    replaceUser,
    replaceUserInfo,
    replaceTenant,
    resetUserContext,
  ]);

  // Block the entire shell while MSAL is interacting or global-user sync is in flight.
  // Entra redirectUri is /app/home, so without this gate App mounts before headers are ready.
  if (
    inProgress !== InteractionStatus.None ||
    isBootstrapping ||
    (hasMsalAccount && !isUserLoaded)
  ) {
    return <SessionLoader />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to="/elystrion-login" replace />} />
        <Route path="/elystrion-login" element={<LoginRedirect />} />
        <Route
          path="blockchain-explorer/:hashid"
          element={<BlockchainDetailsPage data={null} />}
        />
        <Route
          path="/app/*"
          element={
            isUserLoaded ? (
              <MsalAuthenticationTemplate
                interactionType={InteractionType.Redirect}
                authenticationRequest={loginRequest}
              >
                <App />
              </MsalAuthenticationTemplate>
            ) : (
              <Navigate to="/elystrion-login" replace />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default AuthenticationLayer;
